import { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useI18n } from '../i18n';
import type { Locale } from '../i18n';
import { LIVE_LOCALES, LOCALE_NATIVE_NAMES } from '../i18n/locales';
import { swapLocaleInPath } from '../i18n/routes';
import './LangToggle.css';

export function LangToggle() {
  const { locale, setLocale } = useI18n();
  const navigate = useNavigate();
  const { pathname, search, hash } = useLocation();
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const switchTo = (target: Locale) => {
    setLocale(target);
    setOpen(false);
    const newPath = swapLocaleInPath(pathname, target);
    if (newPath !== pathname) navigate(`${newPath}${search}${hash}`, { replace: false });
  };

  return (
    <div className="lang-toggle-wrap" ref={wrapRef}>
      <button
        className="global-ctrl-btn lang-toggle-btn icon-btn"
        onClick={() => setOpen(v => !v)}
        title={locale.toUpperCase()}
        aria-label={locale.toUpperCase()}
        aria-expanded={open}
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <circle cx="12" cy="12" r="10"/>
          <line x1="2" y1="12" x2="22" y2="12"/>
          <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
        </svg>
      </button>
      {open && (
        <div className="lang-dropdown">
          {LIVE_LOCALES.map(code => (
            <button
              key={code}
              className={`lang-option${locale === code ? ' lang-option--active' : ''}`}
              onClick={() => switchTo(code)}
            >
              {LOCALE_NATIVE_NAMES[code]}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
